import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import Paginator from "../../components/Paginator";

const STATUS_COLOR = { pending: "#F59E0B", approved: "#10B981", rejected: "#EF4444" };
const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle };

export default function AdminFinance() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [tab, setTab] = useState("withdrawals");
  const [loading, setLoading] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [networks, setNetworks] = useState({});
  const [wdPage, setWdPage] = useState(1);
  const [wdPerPage, setWdPerPage] = useState(25);
  const [wdPagination, setWdPagination] = useState({ total: 0, total_pages: 1 });
  const [dpPage, setDpPage] = useState(1);
  const [dpPerPage, setDpPerPage] = useState(25);
  const [dpPagination, setDpPagination] = useState({ total: 0, total_pages: 1 });

  useEffect(() => {
    API.get("/admin/withdrawals", { params: { page: wdPage, per_page: wdPerPage } })
      .then(r => { setWithdrawals(r.data.withdrawals || []); setNetworks(r.data.deposit_networks || {}); setWdPagination({ total: r.data.total || 0, total_pages: r.data.total_pages || 1 }); })
      .catch(() => {});
  }, [wdPage, wdPerPage]);

  useEffect(() => {
    API.get("/admin/deposits", { params: { page: dpPage, per_page: dpPerPage } })
      .then(r => { setDeposits(r.data.deposits || []); setDpPagination({ total: r.data.total || 0, total_pages: r.data.total_pages || 1 }); })
      .catch(() => {});
  }, [dpPage, dpPerPage]);

  const actionWithdrawal = async (id, status) => {
    setLoading(`wd-${id}`);
    try {
      const { data } = await API.post(`/admin/withdrawals/${id}/action`, { status });
      toast.success(data.message);
      setWithdrawals(prev => prev.map(w => w.withdrawal_id === id ? { ...w, status } : w));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const actionDeposit = async (id, status) => {
    setLoading(`dp-${id}`);
    try {
      const { data } = await API.post(`/admin/deposits/${id}/action`, { status });
      toast.success(data.message);
      setDeposits(prev => prev.map(d => d.deposit_id === id ? { ...d, status } : d));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const deleteRecord = async (id, type) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await API.delete(`/admin/${type}/${id}`);
      toast.success("Record deleted");
      if (type === "withdrawals") setWithdrawals(prev => prev.filter(w => w.withdrawal_id !== id));
      else setDeposits(prev => prev.filter(d => d.deposit_id !== id));
    } catch { toast.error("Failed"); }
  };

  const pendingWd = withdrawals.filter(w => w.status === "pending").length;
  const pendingDp = deposits.filter(d => d.status === "pending").length;

  return (
    <AdminLayout title="Financial Control">
      <div className="space-y-6" data-testid="admin-finance">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 text-center">
            <p className="font-mono-game text-xl font-bold text-[#F59E0B]">{pendingWd}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Pending WD</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 text-center">
            <p className="font-mono-game text-xl font-bold text-[#B026FF]">{pendingDp}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Pending Dep</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 text-center">
            <p className="font-mono-game text-xl font-bold text-[#10B981]">{withdrawals.filter(w => w.status === "approved").length}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Approved WD</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 text-center">
            <p className="font-mono-game text-xl font-bold text-[#00F0FF]">{deposits.filter(d => d.status === "approved").length}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Approved Dep</p>
          </div>
        </div>

        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {["withdrawals", "deposits"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`finance-tab-${t}`}
              className={`flex-1 py-2 text-sm font-bold uppercase tracking-widest rounded-md transition-all ${tab === t ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
              {t} {t === "withdrawals" && pendingWd > 0 ? `(${pendingWd})` : t === "deposits" && pendingDp > 0 ? `(${pendingDp})` : ""}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-2">
          {(tab === "withdrawals" ? withdrawals : deposits).map((item) => {
            const id = item.withdrawal_id || item.deposit_id;
            const SIcon = STATUS_ICON[item.status] || Clock;
            const sColor = STATUS_COLOR[item.status] || "#94A3B8";
            const isExpanded = expanded === id;

            return (
              <div key={id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`finance-item-${id}`}>
                <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[#1C212E]/30" onClick={() => setExpanded(isExpanded ? null : id)}>
                  <SIcon className="w-5 h-5 flex-shrink-0" style={{ color: sColor }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-[#F8FAFC]">${item.amount}</span>
                      {item.crypto_amount != null && item.currency && <span className="text-xs font-mono-game text-[#00F0FF]">({parseFloat(item.crypto_amount).toFixed(8)} {item.currency})</span>}
                      {item.currency && <span className="text-[10px] font-mono-game px-1.5 py-0.5 rounded bg-[#0B0E14] text-[#00F0FF]">{item.currency}</span>}
                      {item.method && <span className="text-[10px] font-mono-game px-1.5 py-0.5 rounded bg-[#0B0E14] text-[#B026FF]">{item.method}</span>}
                      {item.package_name && <span className="text-[10px] text-[#FFD600]">{item.package_name}</span>}
                      {item.is_suspicious && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B] font-bold" data-testid={`finance-suspicious-badge-${id}`}>SUSPICIOUS</span>}
                    </div>
                    <p className="text-xs text-[#64748B]">User: {item.user_email || item.user_name || `${item.user_id?.slice(0, 10)}...`} | {item.created_at ? new Date(item.created_at).toLocaleString() : ""}</p>
                  </div>
                  <span className="text-xs font-bold uppercase" style={{ color: sColor }}>{item.status}</span>
                  {item.status === "pending" && (
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => tab === "withdrawals" ? actionWithdrawal(id, "approved") : actionDeposit(id, "approved")} disabled={!!loading}
                        className="p-2 text-[#10B981] hover:bg-[#10B981]/10 rounded-lg"><CheckCircle className="w-4 h-4" /></button>
                      <button onClick={() => tab === "withdrawals" ? actionWithdrawal(id, "rejected") : actionDeposit(id, "rejected")} disabled={!!loading}
                        className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg"><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-[#64748B]" /> : <ChevronDown className="w-4 h-4 text-[#64748B]" />}
                  <button onClick={(e) => { e.stopPropagation(); deleteRecord(id, tab); }} className="p-1 text-[#64748B] hover:text-[#EF4444] rounded" data-testid={`delete-record-${id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-[#2A3241]/30 animate-slide-up">
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="bg-[#0B0E14] rounded-lg p-3">
                        <p className="text-[10px] text-[#64748B] uppercase mb-1">User ID</p>
                        <p className="text-xs font-mono-game text-[#F8FAFC] break-all">{item.user_id}</p>
                        {(item.user_email || item.user_name) && <p className="text-[10px] text-[#94A3B8] mt-1 break-all">{item.user_name} {item.user_email ? `<${item.user_email}>` : ""}</p>}
                        {item.is_suspicious && <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B] font-bold">SUSPICIOUS</span>}
                      </div>
                      <div className="bg-[#0B0E14] rounded-lg p-3">
                        <p className="text-[10px] text-[#64748B] uppercase mb-1">Amount</p>
                        <p className="text-sm font-mono-game text-[#FFD600]">${item.amount} {item.currency || ""}</p>
                        {item.crypto_amount != null && <p className="text-xs font-mono-game text-[#00F0FF] mt-0.5">{parseFloat(item.crypto_amount).toFixed(8)} {item.currency}</p>}
                      </div>
                      {(item.fee != null || item.net_amount != null) && (
                        <div className="bg-[#0B0E14] rounded-lg p-3">
                          <p className="text-[10px] text-[#64748B] uppercase mb-1">Fee / Net</p>
                          {item.fee != null && <p className="text-xs font-mono-game text-[#EF4444]">Fee: ${item.fee}</p>}
                          {item.net_amount != null && <p className="text-xs font-mono-game text-[#10B981]">Net: ${item.net_amount}</p>}
                        </div>
                      )}
                      {item.wallet_address && (
                        <div className="bg-[#0B0E14] rounded-lg p-3 col-span-2">
                          <p className="text-[10px] text-[#64748B] uppercase mb-1">Wallet Address {item.currency && <span className="text-[#00F0FF] normal-case">({networks[item.currency] || `${item.currency} mainnet`})</span>}</p>
                          <p className="text-xs font-mono-game text-[#00F0FF] break-all">{item.wallet_address}</p>
                        </div>
                      )}
                      {item.proof && (
                        <div className="bg-[#0B0E14] rounded-lg p-3 col-span-2 border border-[#FFD600]/20">
                          <p className="text-[10px] text-[#FFD600] uppercase mb-1">Transaction Proof</p>
                          <p className="text-xs font-mono-game text-[#F8FAFC] break-all">{item.proof}</p>
                        </div>
                      )}
                      {item.coins_to_credit && (
                        <div className="bg-[#0B0E14] rounded-lg p-3">
                          <p className="text-[10px] text-[#64748B] uppercase mb-1">Coins to Credit</p>
                          <p className="text-sm font-mono-game text-[#FFD600]">{item.coins_to_credit}{item.bonus ? ` + ${item.bonus} bonus` : ""}</p>
                        </div>
                      )}
                      {item.faucetpay_payout_id && (
                        <div className="bg-[#0B0E14] rounded-lg p-3">
                          <p className="text-[10px] text-[#64748B] uppercase mb-1">FaucetPay Payout</p>
                          <p className="text-xs font-mono-game text-[#10B981]">#{item.faucetpay_payout_id}</p>
                        </div>
                      )}
                      <div className="bg-[#0B0E14] rounded-lg p-3">
                        <p className="text-[10px] text-[#64748B] uppercase mb-1">Date</p>
                        <p className="text-xs text-[#94A3B8]">{item.created_at ? new Date(item.created_at).toLocaleString() : ""}</p>
                      </div>
                      {item.processed_at && (
                        <div className="bg-[#0B0E14] rounded-lg p-3">
                          <p className="text-[10px] text-[#64748B] uppercase mb-1">Processed</p>
                          <p className="text-xs text-[#94A3B8]">{new Date(item.processed_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {(tab === "withdrawals" ? withdrawals : deposits).length === 0 && <p className="text-[#94A3B8] text-center py-8">No {tab}</p>}
        </div>
        {tab === "withdrawals" ? (
          <Paginator page={wdPage} totalPages={wdPagination.total_pages} total={wdPagination.total}
            perPage={wdPerPage} onPerPageChange={(n) => { setWdPerPage(n); setWdPage(1); }}
            onPageChange={setWdPage} testIdPrefix="withdrawals-pager" />
        ) : (
          <Paginator page={dpPage} totalPages={dpPagination.total_pages} total={dpPagination.total}
            perPage={dpPerPage} onPerPageChange={(n) => { setDpPerPage(n); setDpPage(1); }}
            onPageChange={setDpPage} testIdPrefix="deposits-pager" />
        )}
      </div>
    </AdminLayout>
  );
}
